import type { Messages } from './en';

/**
 * Fase C: viatges en tren — Taipei (101, MRT, Museu del Palau) i Hualien (Taroko, Qixingtan).
 * Les línies de pràctica d'anglès es queden en anglès expressament.
 */
export const caTrips: Pick<Messages, 'trips'> = {
  trips: {
    mapNames: { station: 'Estació de tren', taipei: 'Taipei', mrt: 'Estació del MRT (metro)', museum: 'Museu del Palau Nacional', hualien: 'Hualien', hotel: 'Hotel vora el mar' },
    zones: {
      station: 'Estació de tren', taipei: 'Taipei', tower101: 'Taipei 101', taipeiStation: 'Estació Central de Taipei', taipeiPark: 'Parc de la ciutat',
      mrt: 'Estació del MRT (metro)', museum: 'Museu del Palau Nacional', gorge: 'Sender de Taroko', taroko: 'Gorja de Taroko', beach: 'Platja de Qixingtan', hualien: 'Hualien', hotel: 'Hotel vora el mar',
    },
    objects: {
      stTicket: 'Taquilla', stTrain: 'Tren', twElevator: 'Mirador del Taipei 101', tpTrain: 'Estació Central de Taipei', tpNoodles: 'Parada de sopa de fideus amb vedella',
      tpCakes: 'Botiga de pastissets de pinya', ubike: 'Lloguer de YouBike', mrtMap: 'Mapa de línies del MRT', mrtRules: 'Normes del MRT', mrtRide: 'Andana del MRT',
      muCabbage: 'Col de jade', muMeat: 'Pedra en forma de carn', muDing: 'Ding de bronze', muRuware: 'Bol de ceràmica Ru', muScroll: 'Rotlle de Qingming',
      muCurio: 'Caixa de tresors', muExit: 'Sortida al MRT', hlHelmet: 'Préstec de cascos', hlView: 'Mirador de la Gruta de les Orenetes', hlStones: 'Torres de pedres',
      hlSea: "L'oceà Pacífic", hlMochi: 'Botiga de mochi', hlTrain: 'Estació de Hualien',
      hlQingshui: 'Mirador dels penya-segats de Qingshui', hlNineTurns: 'Túnel dels Nou Revolts', hlPancake: 'Creps bomba de ceba tendra', hlWonton: 'Parada de sopa de wonton', hlAmis: 'Dansa amis de la collita', hlWhale: 'Vaixell per veure balenes', htDesk: "Recepció de l'hotel", htBed: "El teu llit de l'hotel",
    },
    npc: { stationClerk: 'Taquillera', guide: 'Guia de la ciutat', busker: 'Músic de carrer', docent: 'Guia del museu', ranger: 'Guarda del parc', hiker: 'Excursionista', mochiAuntie: 'La tieta del mochi', captain: 'Capità del vaixell', receptionist: 'Recepcionista' },

    station: {
      counter: 'Good morning! Where would you like to go today?',
      dest: { taipei: 'Taipei', hualien: 'Hualien' },
      destHint: { taipei: 'El Taipei 101, el MRT i el Museu del Palau Nacional', hualien: 'La Gorja de Taroko i la platja de Qixingtan' },
      price: (n: number) => `${n} monedes (anada i tornada)`,
      howAsk: 'Com ho demanes? (pràctica d\'anglès)',
      ask: ['One return ticket to {place}, please.', 'Ticket. {place}. Now.', 'I want to go, give me train.'],
      wrong: 'La taquillera somriu: «Ho podries demanar amb una mica més d\'educació?»',
      bought: (place: string) => `🎫 Has comprat un bitllet d'anada i tornada a ${place}. Ves a l'andana!`,
      poor: 'No tens prou monedes per a aquest bitllet.',
      tooLate: "L'últim tren del dia ja ha sortit. Torna demà al matí!",
      haveTicket: (place: string) => `Ja tens un bitllet a ${place}. L'andana és just passades les barreres.`,
      noTicket: 'Primer necessites un bitllet: compra\'l a la taquilla.',
      board: '🚆 Pujar al tren',
      boardBack: '🚆 Agafar el tren de tornada a casa',
      notNow: 'Ara no',
      trainText: (place: string) => `El tren a ${place} espera a l'andana. Les portes són obertes!`,
      backText: 'El teu tren de tornada a casa t\'espera. A punt per tornar?',
    },
    ride: {
      title: '🚆 Finestreta del tren',
      toTaipei: ['Els arrossars brillen amb el sol del matí…', 'Passen volant poblets, temples i motos…', 'Edificis alts… i allà hi ha el Taipei 101!'],
      toHualien: ['A la dreta s\'alcen muntanyes verdes…', 'Entrem en un túnel… i sortim arran de mar!', 'Penya-segats grisos cauen a plom sobre el Pacífic blau…'],
      home: ['El tren es gronxa suaument…', 'Fas una becaina mirant per la finestra…', 'Ja gairebé ets a casa!'],
      announce: (place: string) => `“The next station is ${place}.”`,
      skip: '⏩ Saltar',
      arrive: (place: string) => `Benvingut a ${place}!`,
    },
    taipei: {
      elevator: "L'ascensor al mirador de la planta 89 és un dels més ràpids del món: 37 segons!",
      goUp: '🛗 Pujar fins a dalt (20 monedes)',
      poor: 'Necessites 20 monedes per al mirador.',
      viewTitle: '🏙️ Taipei 101 · planta 89',
      viewText: [
        'Tota la ciutat és als teus peus: rius, ponts, muntanyes i cotxes diminuts.',
        "Al mig de la torre penja una bola daurada gegant: l'amortidor de massa. Manté la torre estable durant els tifons i els terratrèmols.",
      ],
      viewAsk: 'Respon en anglès: what keeps the tower steady?',
      viewOptions: ['A giant golden ball', 'A long rope', 'Big windows'],
      viewRight: "✅ Correcte! L'amortidor pesa ni més ni menys que 660 tones.",
      viewWrong: '❌ No ben bé… fixa\'t en la bola daurada!',
      noodles: { vendor: 'Welcome! Beef noodle soup — spicy or clear broth?', ok: 'Clear broth, please. One small bowl.', bad: ['Noodles! Hurry!', 'Spicy, but not spicy, but spicy.'], done: 'Vedella tendra, fideus elàstics i un brou boníssim. Et sents superfort!', price: 45 },
      cakes: { vendor: 'Our pineapple cakes are fresh today! Would you like a gift box?', ok: 'Yes, please. A small box for my family, thank you!', bad: ['Cake. Box. Give.', 'I want all of them for free.'], done: 'Una capsa preciosa de pastissets de pinya. A la teva família li encantaran!', price: 40 },
      ubike: 'YouBike compartides: passa la targeta, agafa una bici i torna-la a qualsevol estació.',
      rent: '🚲 Llogar una bici (5 monedes)',
      ride: 'Vas amb YouBike! Recorda: atura\'t als semàfors en vermell.',
      returnBike: '🚲 Tornar la bici',
      returned: 'Tornes la bici. Pip!',
      onlyHere: 'Tornes la YouBike abans de marxar.',
      guide: [
        'Benvingut a Taipei! El MRT (metro) és la manera més fàcil de moure\'s.',
        'Tasta la sopa de fideus amb vedella: cada família té la seva recepta secreta.',
        'Des del mirador del 101 es veuen les muntanyes que envolten la ciutat.',
        'Perdona, em dius com arribar al MRT? Fàcil: travessa el carrer i gira a l\'esquerra!',
      ],
      busker: "Un músic de carrer toca una melodia dolça amb l'erhu, un violí de dues cordes.",
      listen: '🎶 Escoltar i aplaudir',
      listened: 'Aplaudeixes al ritme. El músic fa una reverència i somriu!',
    },
    mrt: {
      map: 'Línies de colors creuen tota la ciutat: vermella, blava, verda, taronja, marró…',
      mapQuiz: 'Practica l\'anglès: “Which line goes to the zoo?”',
      mapOptions: ['The brown line', 'The blue line', 'The bus'],
      mapRight: '✅ Sí! La línia marró va al zoo de Taipei.',
      mapWrong: 'Mmm, torna a mirar el mapa!',
      rules: 'Normes del MRT: llegeix-les en anglès!',
      rulesQs: [
        { q: 'Can you eat or drink on the MRT?', a: ['No, eating and drinking are not allowed.', 'Yes, a picnic is fine.', 'Only ice cream.'] },
        { q: 'Who are the dark blue priority seats for?', a: ['Elderly people, pregnant women, people with children or injuries.', 'Anyone who runs fast.', 'Only the driver.'] },
        { q: 'Where do you stand on the escalator?', a: ['On the right, so people can walk on the left.', 'In the middle, with my arms out.', 'I sit down on it.'] },
      ],
      rulesRight: '✅ Correcte!',
      rulesWrong: '❌ Torna-ho a provar!',
      rulesDone: 'Et saps les normes del MRT com algú d\'aquí!',
      platform: "Les portes de vidre de l'andana s'obriran quan arribi el tren. On vas?",
      toMuseum: '🏛️ Shilin — Museu del Palau Nacional',
      toTower: '🏙️ Taipei 101 / World Trade Center',
      toXimen: '🛍️ Ximen — Ximending',
      toLongshan: '🏮 Temple de Longshan — Wanhua',
      toCks: '🏯 Memorial de Chiang Kai-shek — Plaça de la Llibertat',
      toBeitou: '♨️ Xinbeitou — aigües termals de Beitou',
      riding: 'Ding-dong! “Please mind the gap.”',
    },
    museum: {
      intro: "Practica l'anglès: llegeix cada cartell. La guia et farà una pregunta sobre cada un.",
      items: {
        muCabbage: {
          label: 'Jadeite Cabbage. Carved from one piece of jade, half green and half white. Look closely: two little insects hide on the leaves!',
          q: 'What is hiding on the cabbage?',
          a: ['Two little insects', 'A cat', 'A golden coin'],
        },
        muMeat: {
          label: 'Meat-shaped Stone. It looks just like a piece of braised pork, but it is a stone called jasper. The skin even has tiny pores!',
          q: 'What is the “meat” really made of?',
          a: ['Stone', 'Real pork', 'Chocolate'],
        },
        muDing: {
          label: 'Bronze ding. A cooking pot with three legs, almost three thousand years old. It has a long text written inside.',
          q: 'How many legs does a ding have?',
          a: ['Three', 'Four', 'None'],
        },
        muRuware: {
          label: 'Ru ware basin. Sky-blue pottery with fine cracks in the glaze. Fewer than one hundred pieces exist in the whole world.',
          q: 'What colour is Ru ware?',
          a: ['Sky blue', 'Bright red', 'Black'],
        },
        muScroll: {
          label: 'Along the River During the Qingming Festival. A painting more than eleven metres long, full of boats, bridges, shops and thousands of tiny people.',
          q: 'What can you see in the painting?',
          a: ['Boats, bridges and tiny people', 'Only one big tree', 'A football match'],
        },
        muCurio: {
          label: 'Curio box. A small wooden box with secret drawers, made for an emperor to keep his tiny treasures.',
          q: 'Who was the box made for?',
          a: ['An emperor', 'A fisherman', 'A cat'],
        },
      },
      right: '✅ Molt ben llegit!',
      wrong: '❌ Torna a llegir el cartell!',
      seen: (n: number, total: number) => `Tresors estudiats: ${n}/${total}`,
      exit: 'Agafes el MRT per tornar a la ciutat?',
      ride: '🚇 Agafar el MRT',
      docent: [
        'Benvingut al Museu del Palau Nacional. Aquests tresors tenen centenars, i fins i tot milers, d\'anys.',
        'Si us plau, no facis servir el flaix quan facis fotos. La llum pot fer malbé les pintures antigues.',
        'La nostra peça més famosa? La Col de jade. Tothom vol veure els insectets!',
      ],
    },
    shuttle: {
      stop: 'Bus turístic de Taiwan — ruta de Taroko',
      ask: 'Excuse me, does this bus go to Taroko Gorge?',
      where: 'On vols anar?',
      city: "🏙️ Estació de Hualien · Dongdamen · Ji'an",
      taroko: '⛰️ Gorja de Taroko',
      qixingtan: '🌊 Platja de Qixingtan',
      riding: "L'autobús serpenteja per la carretera de la costa…",
      here: 'Ja hi ets!',
    },
    hualien: {
      helmet: 'A la gorja hi poden caure pedres. Als senders tothom porta casc!',
      rent: '⛑️ Demanar un casc en préstec (gratis)',
      wearing: 'Et poses un casc groc llampant. La seguretat és el primer!',
      already: 'Ja portes un casc posat.',
      needHelmet: '⛑️ El guarda t\'atura: «Posa\'t el casc al sender, si us plau!»',
      view: 'Gruta de les Orenetes: els penya-segats són plens de foradets on les orenetes fan els nius. El riu brama molt avall.',
      look: '🔭 Mirar amb atenció',
      looked: "Les orenetes entren i surten dels forats. L'aigua és turquesa per la pols de marbre!",
      viewHelmet: "Aquí hauries de portar casc. Demana'n un en préstec al centre de visitants.",
      stones: 'Hi ha còdols llisos per tot arreu. La gent els apila en torretes.',
      stack: '🪨 Apilar pedres',
      sea: "L'oceà Pacífic. La terra més propera en aquella direcció és… molt, molt lluny!",
      listen: '🌊 Escoltar les onades',
      listened: 'Fsss… crrrsh… els còdols rodolen amb cada onada. Que relaxant.',
      welcome: '花蓮歡迎您 — Benvingut a Hualien! Muntanyes a una banda i el Pacífic a l’altra.',
      qingshui: 'Penya-segats de Qingshui: les muntanyes verdes cauen de cop dins l’oceà Pacífic blau, gairebé mil metres! Hi ha una carretera excavada al penya-segat.',
      lookCliff: '🏔️ Mira al llarg de la costa',
      lookedCliff: 'El mar és blau fosc vora els penya-segats i turquesa a prop de la riba. A sota hi corre una llarga línia blanca d’onades.',
      nineTurns: 'El Túnel dels Nou Revolts: el sender serpenteja pel marbre com una serp. Mira amunt: els penya-segats gairebé es toquen!',
      // English practice: food orders below.
      pancake: { vendor: 'Bomb scallion pancake! With an egg inside — spicy sauce or not?', ok: 'A little spicy sauce, please. One with an egg!', bad: ['Pancake. Egg. Quick.', 'Make it boom!'], done: 'Cruixent, fullada i amb l’ou sucós per dins. Crema, crema, crema… però és boníssima!', price: 35 },
      wonton: { vendor: 'Hualien wonton soup — thin skins, pork inside. Small or large bowl?', ok: 'A small bowl, please. Thank you!', bad: ['Soup! Big! Now!', 'Wonton, no soup, no wonton.'], done: 'Wontons suaus en una sopa lleugera i clara amb api. Un clàssic de Hualien!', price: 40 },
      amis: 'Els amis, el poble indígena més gran de Taiwan, ballen a la festa de la collita d’estiu (Ilisin). Es donen les mans en un gran cercle i canten junts.',
      watchDance: '💃 Mira i pica de mans',
      danced: 'Una senyora et somriu i t’estira dins del cercle! Pas, pas, puntada… Aprens els primers passos del ball.',
      whale: 'Des del port de Hualien surten excursions per veure balenes. Els dofins hi viuen tot l’any i de vegades hi passen balenes.',
      boat: (n: number) => `⛴️ Puja al vaixell (${n} monedes, 1 hora)`,
      sightings: [
        'Dofins acròbates! N’hi ha desenes que salten i giren al costat del vaixell. Tothom aplaudeix!',
        'Un catxalot enorme treu l’aire —pfffff!— i ensenya la cua abans de submergir-se.',
        'Els peixos voladors freguen les onades i una família de dofins mulars neda al costat del vaixell.',
      ],
      seaFact: 'Pràctica d’anglès: “Dolphins are mammals. They breathe air, just like us.”',
      hotel: {
        desk: 'Welcome to the Seaside Hotel! Would you like a room for tonight?',
        price: (n: number) => `🛏️ Reserva una habitació (${n} monedes)`,
        howAsk: 'Com ho demanes? (pràctica d’anglès)',
        ask: ['I’d like a room for one night, please.', 'Room. Now. One.', 'Give me the biggest room for free.'],
        wrong: 'La recepcionista somriu: «Ho podries demanar amb una mica més d’educació?»',
        booked: '🔑 Aquí tens la clau: habitació 302, amb vistes al mar. Dorm aquí aquesta nit i demà agafa el tren de tornada a casa!',
        already: 'Ja tens habitació per a aquesta nit. La 302 és passada la porta de la dreta.',
        poor: 'No tens prou monedes per a una habitació.',
        bed: 'Un llit tou amb llençols blancs i nets, i el so de les onades per la finestra.',
        sleep: '😴 A dormir',
        noRoom: 'Aquest llit és de l’hotel. Primer reserva una habitació a la recepció.',
        wake: '🌅 Bon dia des de Hualien! El sol surt sobre el Pacífic. El teu bitllet de tren a casa encara és vàlid.',
      },
      mochi: { vendor: 'Hualien mochi! Peanut, sesame or red bean?', ok: 'Peanut, please. Can I have two?', bad: ['Mochi now.', 'All flavours, no money.'], done: "Tou, elàstic i dolç. El berenar preferit de Hualien!", price: 25 },
      ranger: [
        'Taroko vol dir «magnífic i bonic» en la llengua del poble truku, que viu aquí.',
        'No surtis del sender i no et treguis el casc, si us plau.',
        'Aquest marbre té milions d\'anys. El riu va excavar aquesta gorja a poc a poc, dia rere dia.',
      ],
      hiker: [
        'He fet tot el sender! Tinc les cames cansades, però les vistes han valgut la pena.',
        'Has vist el pont penjant? Es gronxa una mica… que divertit!',
        'A Qixingtan pots veure la sortida del sol sobre l\'oceà.',
      ],
    },
  },
};
